'use client';
/**
 * Three plain-English writeups — elevator pitch, walkthrough, technical
 * addendum. Source text lives in content/rating-writeups.ts; edit there.
 * Each writeup is a multi-paragraph string; this component splits on blank
 * lines into <p> tags.
 *
 * Steve directive 2026-05-22: "we should start saving under rating,
 * writeups and i can at least look at them and revise them over time."
 */

import { useState } from 'react';
import {
  ELEVATOR_60S,
  WALKTHROUGH_5MIN,
  TECHNICAL_ADDENDUM,
} from '@/content/rating-writeups';

type Tab = 'elevator' | 'walkthrough' | 'technical';

const TABS: Array<{ key: Tab; label: string; sub: string }> = [
  { key: 'elevator',    label: '60-second',   sub: 'Cocktail party / allocator first meeting' },
  { key: 'walkthrough', label: '5-minute',    sub: 'Diligence call / prospective LP' },
  { key: 'technical',   label: 'Technical',   sub: 'Quant / allocator-with-CIO' },
];

const CONTENT: Record<Tab, string> = {
  elevator: ELEVATOR_60S,
  walkthrough: WALKTHROUGH_5MIN,
  technical: TECHNICAL_ADDENDUM,
};

function renderProse(text: string) {
  const paragraphs = text.split(/\n\s*\n/).map(p => p.trim()).filter(Boolean);
  return paragraphs.map((p, i) => (
    <p key={i} className="text-[14px] text-[#cbd5e1] leading-relaxed mb-3 last:mb-0">
      {p}
    </p>
  ));
}

export function PlainEnglish() {
  const [active, setActive] = useState<Tab>('elevator');
  return (
    <section className="border border-[var(--gold)]/30 rounded-lg bg-[#0a0e14] p-5 mt-8">
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="text-[var(--gold)] text-[18px] font-semibold">
          Plain English
        </h2>
        <span className="text-[11px] text-[#64748b]">
          Source: <span className="font-mono">content/rating-writeups.ts</span> — edit to revise
        </span>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {TABS.map(t => {
          const isActive = t.key === active;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActive(t.key)}
              className={`text-left px-3 py-2 rounded-md border transition-colors ${
                isActive
                  ? 'bg-[var(--gold)]/15 border-[var(--gold)]/60 text-[var(--gold)]'
                  : 'bg-[#0f141c] border-[#1e293b] text-[#94a3b8] hover:text-white hover:border-[#334155]'
              }`}
            >
              <div className="text-[12px] font-semibold tracking-wide uppercase">{t.label}</div>
              <div className="text-[10px] text-[#64748b] mt-0.5">{t.sub}</div>
            </button>
          );
        })}
      </div>

      <div className="bg-[#0f141c] border border-[#1e293b] rounded-md p-5 max-w-4xl">
        {renderProse(CONTENT[active])}
      </div>

      <p className="text-[10px] text-[#64748b] mt-3">
        These writeups are the three audience-specific cuts Steve uses to introduce the platform to outside readers. Plain text (paragraphs separated by blank lines) in the source file, markdown-style emphasis fine as plain text.
      </p>
    </section>
  );
}
